export type ContractTemplateListItem = {
  id: number
  title: string
  url?: string
  is_default?: boolean
  [key: string]: unknown
}

export type PaymentOptionForContract = {
  id: number
  title: string
  payment_type: string
}

export type ContractTemplateOverrides = Record<number | string, number | string>

export type SystemForDisplayName = {
  name?: string
  battery_total_kwh?: number
  module_quantity?: number
}

export type TranslateFn = (phrase: string) => string

export enum ContractTemplateModeEnum {
  project = 'project',
  payment_option = 'payment-option',
}

export type ContractTemplateMode = `${ContractTemplateModeEnum}`

export type ContractTemplateModeChangePayload = {
  mode: ContractTemplateMode
  systemUuids: string[]
}

export type ContractTemplateOverridesUpdatePayload = {
  systemUuid: string
  paymentOptionId: number
  contractId: number | null
}

export const getDefaultContractTemplate = (contracts: ContractTemplateListItem[] | undefined) => {
  if (!contracts?.length) return undefined
  return contracts.find((contract) => contract.is_default === true)
}

export const getDefaultContractTemplateForPaymentOption = (
  contracts: ContractTemplateListItem[] | undefined,
  paymentOption: PaymentOptionForContract,
  orgDefault?: ContractTemplateListItem
) => {
  if (!contracts?.length) return orgDefault
  const prop = `is_default_${paymentOption.payment_type}`
  for (let i = 0; i < contracts.length; i++) {
    if (contracts[i][prop]) {
      return contracts[i]
    }
  }
  return orgDefault ?? getDefaultContractTemplate(contracts)
}

export const getOverrideSelectValue = (
  overrides: ContractTemplateOverrides | undefined,
  paymentOptionId: number
): string => {
  const value = overrides?.[paymentOptionId]
  if (value === undefined || value === '') return ''
  return String(value)
}

export const buildOverridesAfterChange = (
  overrides: ContractTemplateOverrides | undefined,
  paymentOptionId: number,
  contractId: number | null
): ContractTemplateOverrides => {
  const contract_template_overrides = { ...(overrides || {}) }
  if (contractId === null) {
    if (Object.prototype.hasOwnProperty.call(contract_template_overrides, paymentOptionId)) {
      delete contract_template_overrides[paymentOptionId]
    }
  } else {
    contract_template_overrides[paymentOptionId] = contractId
  }
  return contract_template_overrides
}

export const isPaymentOptionContractMode = (contractTemplateMode: string | undefined): boolean => {
  return contractTemplateMode === ContractTemplateModeEnum.payment_option
}

export const buildContractSharingFilter = (
  orgId: number | undefined,
  projectFormValues: { org_id?: number; shared_with?: { org_id: number }[] } | undefined
): Record<string, string | number> => {
  if (!orgId || !projectFormValues?.shared_with?.length) {
    return {}
  }
  const sharedIds = projectFormValues.shared_with.map((item) => item.org_id).join(',')
  return {
    visible_to: orgId,
    owned_by: `${projectFormValues.org_id},${sharedIds}`,
  }
}

export const getSystemDisplayName = (system: SystemForDisplayName, translate: TranslateFn): string => {
  if (system.name && system.name.length > 0) {
    return system.name
  }
  if ((system.battery_total_kwh ?? 0) > 0) {
    if ((system.module_quantity ?? 0) > 0) {
      return `${system.module_quantity ?? 0} ${translate('Panels + Battery')}`
    }
    return `${system.battery_total_kwh ?? 0} ${translate('kWh Battery')}`
  }
  return `${system.module_quantity ?? 0} ${translate('Panels')}`
}
